import './styling/travelStyling.scss';

import { FC } from 'react';
import { Control, FieldErrors } from 'react-hook-form';
import { Box, Title } from '@gjensidige/builders-components';

import { Datepicker, Dropdown, Input, InputGroup, Textarea } from '../../../components';
import {
  delayDurationOptions,
  delayOrCancellationCauses,
  insuranceUserOptions,
  travelPurposeOptions,
  yesOrNo,
} from '../../../constants';
import { EquinorTravelValues } from '../../../utils/types';
import { TravelHeader } from '../components/TravelHeader';
import { PersonDetails } from './PersonDetails';

interface DelayedOrCancelledTransportationProps {
  control: Control<EquinorTravelValues>;
  errors: FieldErrors<EquinorTravelValues>;
}

const DelayedOrCancelledTransportation: FC<DelayedOrCancelledTransportationProps> = ({
  control,
  errors,
}) => {
  return (
    <>
      <TravelHeader
        headerTitle="Delayed or cancelled transportation"
        description="Your transportation was delayed or cancelled, and you have expenses related to the incident."
      />
      <PersonDetails control={control} errors={errors} />
      <Box variant="form-section" className="form-section">
        <Title as="h4" size="5">
          Claim Details
        </Title>
        <InputGroup
          name="injuredParty"
          control={control}
          labelText="To whom did the delay or cancellation occur"
          options={insuranceUserOptions}
          type="checkbox"
          variant="column"
          tracking={false}
          error={errors.injuredParty}
        />
        <Textarea
          name="otherImpactedParts"
          control={control}
          labelText="If others than you within your travel party were impacted by the delay or cancellation, add their names, birthdates, and your relation to them - optional"
          maxWarning={500}
          error={errors.otherImpactedParts}
        />
        <Datepicker
          name="incidentDate"
          control={control}
          labelText="Which date did the delay occur? (dd/mm/yyyy)"
          format="dd/MM/yyyy"
          formatDayJs="DD/MM/YYYY"
          error={errors.incidentDate}
        />
        <Dropdown
          name="incidentCause"
          labelText="What caused the delay?"
          placeholder="Select an option"
          options={delayOrCancellationCauses}
          control={control}
          error={errors.incidentCause}
          tracking={false}
        />
        <InputGroup
          name="delayedOrCancelledTransportation.delayDuration"
          control={control}
          labelText="For how long was the transport delayed?"
          options={delayDurationOptions}
          type="radio"
          variant="column"
          error={errors.delayedOrCancelledTransportation?.delayDuration}
        />
        <InputGroup
          name="delayedOrCancelledTransportation.customerInformed"
          control={control}
          labelText="Were you informed of the delay before departing from your home address?"
          options={yesOrNo}
          type="radio"
          variant="column"
          error={errors.delayedOrCancelledTransportation?.customerInformed}
        />
        <Input
          name="delayedOrCancelledTransportation.departureCountry"
          control={control}
          labelText="Country of departure"
          type="text"
          tracking={false}
          error={errors.delayedOrCancelledTransportation?.departureCountry}
        />
        <Input
          name="delayedOrCancelledTransportation.arrivalCountry"
          control={control}
          labelText="Country of arrival"
          type="text"
          tracking={false}
          error={errors.delayedOrCancelledTransportation?.arrivalCountry}
        />
        <InputGroup
          name="travelPurpose"
          control={control}
          labelText="What was the purpose of the trip?"
          options={travelPurposeOptions}
          type="radio"
          variant="column"
          tracking={false}
          error={errors.travelPurpose}
        />
        <Datepicker
          name="tripStartBookedDate"
          control={control}
          labelText="When did the trip start? (dd/mm/yyyy)"
          format="dd/MM/yyyy"
          formatDayJs="DD/MM/YYYY"
          error={errors.tripStartBookedDate}
        />
        <Datepicker
          name="tripEndCancelledDate"
          control={control}
          labelText="When did the trip end? (dd/mm/yyyy)"
          helpText="If you are still traveling, choose your planned trip end date"
          format="dd/MM/yyyy"
          formatDayJs="DD/MM/YYYY"
          error={errors.tripEndCancelledDate}
        />
        <Input
          name="compensation"
          control={control}
          labelText="Add the amount you are seeking compensation for"
          type="text"
          tracking={false}
          error={errors.compensation}
        />
        <Textarea
          name="detailedDescriptionCompensation"
          control={control}
          labelText="Provide a detailed description of what kind of expenses you are seeking compensation for"
          maxWarning={500}
          helpText="E.g. additional travel or transportation expenses"
          error={errors.detailedDescriptionCompensation}
        />
      </Box>
    </>
  );
};

export default DelayedOrCancelledTransportation;
